/**
 * An i18next backend that loads translation files as separate Vite chunks.
 *
 * Every locales/<language>/<namespace>.json becomes its own small chunk, so a
 * screen downloads only the namespaces it uses, in the language it is shown
 * in. Nothing is fetched from a translation service at runtime — the files
 * ship with the app.
 *
 * In bilingual mode the SECONDARY language's file for the same namespace is
 * loaded alongside, before the namespace counts as loaded, so a screen never
 * renders half-bilingual and then re-renders.
 *
 * @param {Record<string, () => Promise<{ default: object }>>} loaders
 *   the result of import.meta.glob('./locales/*\/*.json'), keyed by path.
 * @param {() => string | null} secondaryLanguage
 */
export function createLazyBackend(loaders, secondaryLanguage) {
  let i18n = null;

  const load = (language, namespace) => {
    const loader = loaders[`./locales/${language}/${namespace}.json`];
    // A namespace not translated into a language yet is simply empty: i18next
    // then falls back to English rather than failing the screen.
    return loader ? loader().then((module) => module.default ?? module) : Promise.resolve({});
  };

  return {
    type: 'backend',
    init(services) {
      i18n = services?.i18n ?? null;
    },
    /** Lets the caller attach the instance when `services.i18n` is not provided. */
    attach(instance) {
      i18n = instance;
    },
    read(language, namespace, callback) {
      const secondary = secondaryLanguage();
      const companion =
        secondary && secondary !== language && i18n && !i18n.hasResourceBundle(secondary, namespace)
          ? load(secondary, namespace).then((data) => i18n.addResourceBundle(secondary, namespace, data, true, true))
          : Promise.resolve();

      Promise.all([load(language, namespace), companion]).then(
        ([data]) => callback(null, data),
        (error) => callback(error, null)
      );
    },
  };
}
